import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MDBContainer, MDBSpinner } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import { CURRENCY_SYMBOLS, formatMoney } from "./VendorPayoutsPage";
import "./Pnlpage.css";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const API_BASE = API_HOST + "/api/v1";

const COST_CATEGORIES = [
  "Advertising",
  "Shipping",
  "Software & Tools",
  "Packaging",
  "Salaries",
  "Refunds",
  "Other",
];

// ── date helpers ─────────────────────────────────────────────
function pad2(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function thisMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toISODate(start), to: toISODate(end) };
}

function lastMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: toISODate(start), to: toISODate(end) };
}

function thisYearRange() {
  const now = new Date();
  return { from: `${now.getFullYear()}-01-01`, to: toISODate(now) };
}

function rangeLabel(from, to) {
  try {
    const f = new Date(`${from}T00:00:00`);
    const t = new Date(`${to}T00:00:00`);
    const opts = { month: "short", day: "numeric", year: "numeric" };
    return `${f.toLocaleDateString(undefined, opts)} – ${t.toLocaleDateString(undefined, opts)}`;
  } catch {
    return `${from} – ${to}`;
  }
}

// ── money helpers ────────────────────────────────────────────
function formatMoneyMapInline(moneyMap) {
  const entries = Object.entries(moneyMap || {});
  if (!entries.length) return "—";
  return entries.map(([cur, amt]) => formatMoney(amt, cur)).join("   ");
}

function MoneyMap({ moneyMap, tone }) {
  const entries = Object.entries(moneyMap || {});
  if (!entries.length) return <span className="pnl-num pnl-num-muted">—</span>;
  return (
    <span className={`pnl-num ${tone ? `pnl-num-${tone}` : ""}`}>
      {entries.map(([cur, amt], i) => (
        <React.Fragment key={cur}>
          {i > 0 && <span className="pnl-num-sep">+</span>}
          {formatMoney(amt, cur)}
        </React.Fragment>
      ))}
    </span>
  );
}

function addMoneyMap(map, currency, amount) {
  const next = { ...map };
  next[currency] = Math.round(((next[currency] || 0) + amount) * 100) / 100;
  return next;
}

function subtractMoneyMap(a, b) {
  const result = {};
  const currencies = new Set([
    ...Object.keys(a || {}),
    ...Object.keys(b || {}),
  ]);
  for (const cur of currencies) {
    result[cur] = Math.round(((a?.[cur] || 0) - (b?.[cur] || 0)) * 100) / 100;
  }
  return result;
}

function escapeCsvValue(value) {
  if (value == null) return '""';
  return `"${String(value).replace(/"/g, '""')}"`;
}

export default function PnLPage() {
  const { user, companyId } = useAuth();
  const userRole = normalizeRole(user?.role);
  const isAllowed = userRole === "owner" || userRole === "seller manager";

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultRange = thisMonthRange();
  const dateFrom = searchParams.get("from") || defaultRange.from;
  const dateTo = searchParams.get("to") || defaultRange.to;
  const [activePreset, setActivePreset] = useState("month");

  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Local-only operating costs — NOT persisted to any backend.
  // Cleared on page reload; exists purely to let Net Profit be
  // calculated live during this viewing session, and to include in
  // the CSV export as a record.
  const [costs, setCosts] = useState([]);
  const [costForm, setCostForm] = useState({
    category: COST_CATEGORIES[0],
    description: "",
    amount: "",
    currency: "USD",
  });

  const applyRange = (from, to, preset) => {
    setActivePreset(preset || "custom");
    setSearchParams({ from, to });
    setDraftFrom(from);
    setDraftTo(to);
  };

  const applyPreset = (preset) => {
    if (preset === "month")
      applyRange(thisMonthRange().from, thisMonthRange().to, "month");
    else if (preset === "lastMonth")
      applyRange(lastMonthRange().from, lastMonthRange().to, "lastMonth");
    else if (preset === "year")
      applyRange(thisYearRange().from, thisYearRange().to, "year");
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    applyRange(draftFrom, draftTo, "custom");
  };

  const loadPnL = useCallback(async () => {
    if (!companyId || !isAllowed) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        company_id: companyId,
        role: userRole,
        date_from: dateFrom,
        date_to: dateTo,
      });
      const res = await fetch(
        `${API_BASE}/orders/dashboard/pnl?${params.toString()}`,
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error || "Failed to load P&L");
        setData(null);
        return;
      }
      setData(json);
    } catch (err) {
      setError(err.message || "Failed to load P&L");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, isAllowed, userRole, dateFrom, dateTo]);

  useEffect(() => {
    loadPnL();
  }, [loadPnL]);

  const operatingCostsMap = useMemo(() => {
    let map = {};
    for (const c of costs) map = addMoneyMap(map, c.currency, c.amount);
    return map;
  }, [costs]);

  const netProfit = useMemo(() => {
    if (!data) return {};
    return subtractMoneyMap(data.gross_profit, operatingCostsMap);
  }, [data, operatingCostsMap]);

  const addCost = () => {
    const amt = Number(costForm.amount);
    if (!amt || amt <= 0) return;
    setCosts((prev) => [...prev, { id: Date.now(), ...costForm, amount: amt }]);
    setCostForm((f) => ({ ...f, description: "", amount: "" }));
  };

  const removeCost = (id) => {
    setCosts((prev) => prev.filter((c) => c.id !== id));
  };

  const handleExport = () => {
    if (!data) return;
    const lines = [];
    lines.push(["P&L Statement", rangeLabel(dateFrom, dateTo)]);
    lines.push([]);
    lines.push(["Revenue", formatMoneyMapInline(data.revenue)]);
    lines.push(["COGS (Vendor Payout)", formatMoneyMapInline(data.cogs)]);
    lines.push(["Gross Profit", formatMoneyMapInline(data.gross_profit)]);
    lines.push([]);
    lines.push(["Operating Costs (manual, this session)"]);
    costs.forEach((c) => {
      lines.push([
        c.category,
        c.description || "",
        formatMoney(c.amount, c.currency),
      ]);
    });
    lines.push([
      "Total Operating Costs",
      formatMoneyMapInline(operatingCostsMap),
    ]);
    lines.push([]);
    lines.push(["Net Profit", formatMoneyMapInline(netProfit)]);
    lines.push([]);
    lines.push(["By Marketplace"]);
    lines.push([
      "Marketplace",
      "Orders",
      "Items",
      "Revenue",
      "COGS",
      "Gross Profit",
    ]);
    data.by_marketplace.forEach((m) => {
      lines.push([
        m.marketplace,
        m.orders,
        m.items,
        formatMoneyMapInline(m.revenue),
        formatMoneyMapInline(m.cogs),
        formatMoneyMapInline(m.gross_profit),
      ]);
    });
    lines.push([]);
    lines.push(["By Vendor"]);
    lines.push([
      "Vendor",
      "Orders",
      "Items",
      "Revenue",
      "COGS",
      "Gross Profit",
    ]);
    data.by_vendor.forEach((v) => {
      lines.push([
        v.vendor_name,
        v.orders,
        v.items,
        formatMoneyMapInline(v.revenue),
        formatMoneyMapInline(v.cogs),
        formatMoneyMapInline(v.gross_profit),
      ]);
    });

    const csvContent = lines
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `pnl_${dateFrom}_to_${dateTo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isAllowed) {
    return (
      <MDBContainer fluid className="py-4">
        <div className="pnl-denied">
          This report is restricted to Owner and Seller Manager roles.
        </div>
      </MDBContainer>
    );
  }

  return (
    <div className="pnl-root">
      <MDBContainer fluid className="pnl-container">
        <header className="pnl-header">
          <div>
            <p className="pnl-eyebrow">Finance</p>
            <h1 className="pnl-title">Profit &amp; loss</h1>
            <p className="pnl-subtitle">
              Revenue, vendor cost, and gross margin for delivered orders —{" "}
              {rangeLabel(dateFrom, dateTo)}.
            </p>
          </div>
          <button
            type="button"
            className="pnl-btn-primary"
            onClick={handleExport}
            disabled={loading || !data}
          >
            Export CSV
          </button>
        </header>

        {/* ── Date range controls ── */}
        <div className="pnl-toolbar">
          <div className="pnl-preset-group">
            <button
              type="button"
              className={`pnl-preset-btn ${activePreset === "month" ? "active" : ""}`}
              onClick={() => applyPreset("month")}
            >
              This month
            </button>
            <button
              type="button"
              className={`pnl-preset-btn ${activePreset === "lastMonth" ? "active" : ""}`}
              onClick={() => applyPreset("lastMonth")}
            >
              Last month
            </button>
            <button
              type="button"
              className={`pnl-preset-btn ${activePreset === "year" ? "active" : ""}`}
              onClick={() => applyPreset("year")}
            >
              This year
            </button>
          </div>

          <div className="pnl-custom-range">
            <input
              type="date"
              className="pnl-date-input"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
            />
            <span className="pnl-range-sep">to</span>
            <input
              type="date"
              className="pnl-date-input"
              value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
            />
            <button
              type="button"
              className="pnl-apply-btn"
              onClick={applyCustom}
            >
              Apply
            </button>
          </div>
        </div>

        {loading ? (
          <div className="pnl-state">
            <MDBSpinner role="status" />
            <span>Loading P&amp;L…</span>
          </div>
        ) : error || !data ? (
          <div className="pnl-state pnl-state-error">
            <p>{error || "No data available."}</p>
          </div>
        ) : (
          <>
            {/* ── Statement summary ── */}
            <div className="pnl-statement">
              <div className="pnl-statement-row">
                <span className="pnl-statement-label">Revenue</span>
                <MoneyMap moneyMap={data.revenue} />
              </div>
              <div className="pnl-statement-row pnl-statement-sub">
                <span className="pnl-statement-label">
                  Cost of goods sold (vendor payout)
                </span>
                <MoneyMap moneyMap={data.cogs} tone="negative" />
              </div>
              <div className="pnl-statement-row pnl-statement-subtotal">
                <span className="pnl-statement-label">Gross profit</span>
                <MoneyMap moneyMap={data.gross_profit} tone="positive" />
              </div>
              <div className="pnl-statement-row pnl-statement-sub">
                <span className="pnl-statement-label">
                  Operating costs
                  <span className="pnl-unsaved-tag">not saved</span>
                </span>
                <MoneyMap moneyMap={operatingCostsMap} tone="negative" />
              </div>
              <div className="pnl-statement-row pnl-statement-net">
                <span className="pnl-statement-label">Net profit</span>
                <MoneyMap moneyMap={netProfit} tone="positive" />
              </div>
              <p className="pnl-statement-note">
                {data.totals.orders} delivered order
                {data.totals.orders === 1 ? "" : "s"} · {data.totals.items} item
                {data.totals.items === 1 ? "" : "s"} in this range. Figures are
                shown per currency — mixed-currency totals are never converted
                or combined.
              </p>
            </div>

            {/* ── Manual operating costs entry ── */}
            <div className="pnl-card">
              <div className="pnl-card-header">
                <h2>Operating costs</h2>
                <span className="pnl-card-header-note">
                  Entered manually for this session only — not saved to the
                  server.
                </span>
              </div>

              <div className="pnl-cost-form">
                <select
                  className="pnl-select"
                  value={costForm.category}
                  onChange={(e) =>
                    setCostForm((f) => ({ ...f, category: e.target.value }))
                  }
                >
                  {COST_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="pnl-input pnl-input-desc"
                  placeholder="Description (optional)"
                  value={costForm.description}
                  onChange={(e) =>
                    setCostForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="pnl-input pnl-input-amount"
                  placeholder="Amount"
                  value={costForm.amount}
                  onChange={(e) =>
                    setCostForm((f) => ({ ...f, amount: e.target.value }))
                  }
                />
                <select
                  className="pnl-select pnl-select-currency"
                  value={costForm.currency}
                  onChange={(e) =>
                    setCostForm((f) => ({ ...f, currency: e.target.value }))
                  }
                >
                  {Object.keys(CURRENCY_SYMBOLS).map((cur) => (
                    <option key={cur} value={cur}>
                      {cur}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="pnl-add-cost-btn"
                  onClick={addCost}
                >
                  Add
                </button>
              </div>

              {costs.length > 0 ? (
                <table className="pnl-cost-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Description</th>
                      <th className="">Amount</th>
                      <th className="pnl-th-action" />
                    </tr>
                  </thead>
                  <tbody>
                    {costs.map((c) => (
                      <tr key={c.id}>
                        <td>{c.category}</td>
                        <td className="pnl-muted">{c.description || "—"}</td>
                        <td className="pnl-num">
                          {formatMoney(c.amount, c.currency)}
                        </td>
                        <td className="pnl-th-action">
                          <button
                            type="button"
                            className="pnl-remove-btn"
                            onClick={() => removeCost(c.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="pnl-empty-note">
                  No operating costs added for this session yet.
                </p>
              )}
            </div>

            {/* ── By marketplace ── */}
            <div className="pnl-card">
              <div className="pnl-card-header">
                <h2>By marketplace</h2>
              </div>
              {data.by_marketplace.length === 0 ? (
                <p className="pnl-empty-note">
                  No delivered orders in this range.
                </p>
              ) : (
                <table className="pnl-table">
                  <thead>
                    <tr>
                      <th>Marketplace</th>
                      <th className="">Orders</th>
                      <th className="">Items</th>
                      <th className="">Revenue</th>
                      <th className="">COGS</th>
                      <th className="">Gross profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_marketplace.map((m) => (
                      <tr key={m.marketplace}>
                        <td className="pnl-cell-title">{m.marketplace}</td>
                        <td className="pnl-num">{m.orders}</td>
                        <td className="pnl-num">{m.items}</td>
                        <td className="">
                          <MoneyMap moneyMap={m.revenue} />
                        </td>
                        <td className="">
                          <MoneyMap moneyMap={m.cogs} />
                        </td>
                        <td className="">
                          <MoneyMap moneyMap={m.gross_profit} tone="positive" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── By vendor ── */}
            <div className="pnl-card">
              <div className="pnl-card-header">
                <h2>By vendor</h2>
              </div>
              {data.by_vendor.length === 0 ? (
                <p className="pnl-empty-note">
                  No delivered orders in this range.
                </p>
              ) : (
                <table className="pnl-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th className="">Orders</th>
                      <th className="">Items</th>
                      <th className="">Revenue</th>
                      <th className="">COGS</th>
                      <th className="">Gross profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_vendor.map((v) => (
                      <tr key={v.vendor_id}>
                        <td className="pnl-cell-title">{v.vendor_name}</td>
                        <td className="pnl-num">{v.orders}</td>
                        <td className="pnl-num">{v.items}</td>
                        <td className="">
                          <MoneyMap moneyMap={v.revenue} />
                        </td>
                        <td className="">
                          <MoneyMap moneyMap={v.cogs} />
                        </td>
                        <td className="">
                          <MoneyMap moneyMap={v.gross_profit} tone="positive" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </MDBContainer>
    </div>
  );
}
